import { isNil } from 'lodash-es';
import { FormGroup } from '@angular/forms'
import { ControlModel, ValueControlModel } from './control';
import { Styles, ControlContainerStyles } from './style';


export class HeaderStyles extends Styles {
  private setTextAlignClass(klass: string) {
    this.removeClass([
      'center',
      'right',
      'left']);
    this.addClass(klass);
  }

  setTextAlignCenterClass() { this.setTextAlignClass('center'); }
  setTextAlignRightClass() { this.setTextAlignClass('right'); }
  setTextAlignLeftClass() { this.setTextAlignClass('left'); }
}

export class GridHeaderModel {
  styles: HeaderStyles;
  name: string;
  required: boolean;
  hidden: boolean;
  width: number;

  constructor(styles?: any, name?: string, required?: boolean, hidden?: boolean, width?: number) {
    this.styles = isNil(styles) ? new HeaderStyles() : styles;
    this.name = name;
    this.required = required;
    this.hidden = hidden ?? false;
    this.width = width;
  }
}

export class CellStyles extends ControlContainerStyles {
  setHyperlinkClass(enabled: boolean) { enabled ? this.addClass('grid-table-cell-link') : this.removeClass('grid-table-cell-link') }

  private setTextAlignClass(klass: string) {
    this.removeClass([
      'center',
      'right',
      'left']);
    this.addClass(klass);
  }

  setTextAlignCenterClass() { this.setTextAlignClass('center'); }
  setTextAlignRightClass() { this.setTextAlignClass('right'); }
  setTextAlignLeftClass() { this.setTextAlignClass('left'); }
}

export class GridCellModel<TDC extends ControlModel, TEC extends ControlModel | void = void> {
  styles: CellStyles;
  mode = 'display';
  displayControl: TDC;
  editControl: TEC;

  constructor(styles: CellStyles, displayControl: TDC, editControl?: TEC) {
    this.styles = isNil(styles) ? new CellStyles() : styles;
    this.displayControl = displayControl;
    this.editControl = editControl;
  }

  setDisplayMode() {
    this.mode = 'display';
  }

  setEditMode() {
    if (this.editControl) {
      this.mode = 'edit';
    }
  }

  get isDisplay() {
    return this.mode === 'display';
  }

  get isEdit() {
    return this.mode === 'edit';
  }

  get isEditable(): boolean {
    return this.editControl && this.editControl instanceof ValueControlModel
      && !(this.editControl as ValueControlModel).readOnly;
  }

  focus() {
    if (this.isEditable) {
      Promise.resolve(null).then(() => (this.editControl as ValueControlModel).focus());
    }
  }
}

export class GridRowModel {
  styles: Styles;
  rowId: string;

  isNew = false;

  mode: 'display' | 'edit' = 'display';

  expand = false;

  formGroup: FormGroup
  cells: { [key: string]: GridCellModel<ControlModel, ControlModel | void> };

  $propertyChangeCallback: (source: GridRowModel, property: string) => void = null;

  constructor(styles?: any) {
    this.styles = isNil(styles) ? new Styles() : styles;
  }

  $_selected = false;
  get selected(): boolean {
    return this.$_selected;
  }

  set selected(value: boolean) {
    this.$_selected = value;
    if (this.$propertyChangeCallback) {
      this.$propertyChangeCallback(this, 'selected');
    }
  }

  setDisplayMode() {
    this.mode = 'display';
    Object.keys(this.cells).forEach((cellName) => {
      this.cells[cellName].setDisplayMode();
    });
  }

  setEditMode() {
    this.mode = 'edit';
    Object.keys(this.cells).forEach((cellName) => {
      this.cells[cellName].setEditMode();
    });
  }

  get isDisplay() {
    return this.mode === 'display';
  }

  get isEdit() {
    return this.mode === 'edit';
  }

  getFirstEditableCell() {
    for (let cell of Object.keys(this.cells).map((cellName) => this.cells[cellName])) {
      if (cell.isEditable) {
        return cell;
      }
    }
    return null;
  }

  getLastEditableCell() {
    for (let cell of Object.keys(this.cells).reverse().map((cellName) => this.cells[cellName])) {
      if (cell.isEditable) {
        return cell;
      }
    }
    return null;
  }

  focusFirstEditableCell() {
    const cell = this.getFirstEditableCell();
    if (cell) {
      cell.focus();
    }
  }

  focusLastEditableCell() {
    const cell = this.getLastEditableCell();
    if (cell) {
      cell.focus();
    }
  }

  $cellClicked(cellId: string): void { }

  destroy() { }
  async refresh() { }
  async save() { }

  $_inAction = false;
  async confirm() {
    this.$_inAction = true;
    try {
      if (this.formGroup.valid) {
        // TODO: revise
        // expects save flow to throw an error on unsuccessful save
        try {
          if (this.formGroup.dirty || this.isNew) {
            await this.save();
            //await this.refresh(); //moved into save flow
            this.setDisplayMode();
          } else {
            //just close edit mode
            this.setDisplayMode();
          }
        } catch {
          // noop
        }
      } else {
        console.log('invalid form on confirm');
      }
      this.$_inAction = false;;
    } catch (error) {
      this.$_inAction = false;;
      throw error;
    }
  }

  async mediumSave() {
    this.$_inAction = true;
    try {
      if (this.formGroup.valid) {
        // TODO: revise
        // expects save flow to throw an error on unsuccessful save
        try {
          if (this.formGroup.dirty || this.isNew) {
            await this.save();
            //await this.refresh(); //moved into save flow
          }
        } catch {
          // noop
        }
      } else {
        console.log('invalid form on mediumSave');
      }
      this.$_inAction = false;;
    } catch (error) {
      this.$_inAction = false;;
      throw error;
    }
  }

  get $isBusy() {
    return this.$isInAction ||
      (!this.isNew && (this.isChanged)) ||
      (this.isNew && (this.isInvalid || this.isChanged));
  }

  get $isReadyToConfirm() {
    return !this.$isInAction && !this.isInvalid;
  }

  get $isInAction() {
    return this.$_inAction;
  }

  get isInvalid() {
    return !this.formGroup.valid;
  }

  get isChanged() {
    return this.formGroup.dirty;
  }
}

export class GridContainerStyle extends Styles {
  columnSizingType: string;

  constructor(columnSizingType: string, classes?: string[], style?: { [klass: string]: any }) {
    super(classes, style);
    this.columnSizingType = columnSizingType;
  }

  private setStateClass(klass: string) {
    this.removeClass([
      'set-width-table',
      'noscroll-table',
      'fit-content-table'
    ]);

    this.addClass(klass);
  }

  private setRowStylingClass(klass: string) {
    this.removeClass([
      'relaxed',
      'compact',
    ]);

    this.addClass(klass);
  }

  setRelaxedRowClass() { this.setRowStylingClass('relaxed'); }
  setCompactRowClass() { this.setRowStylingClass('compact'); }
  setDefaultRowClass() { this.setRowStylingClass(''); }

  setHeadersWidthClass() { this.columnSizingType = 'headersWidth'; this.setStateClass(''); }
  setFixedWidthClass() { this.columnSizingType = 'fixedWidth'; this.setStateClass('set-width-table'); }
  setFittedWidthClass() { this.columnSizingType = 'fitedWidth'; this.setStateClass('noscroll-table'); }
  setCellsWidthClass() { this.columnSizingType = 'cellsWidth'; this.setStateClass('fit-content-table'); }
}